import React from "react";
import * as R from "ramda";
import * as Yup from "yup";
import styled from "styled-components/macro";
import { Modal, Radio, Select, Input } from "antd";

import {
  FormConfig,
  Form as FormikForm,
  Field,
  ErrorMessage
} from "../../../../../components/controls/FormikForm";
import Message from "../../../../../components/controls/FormikForm/ErrorMessage";

const TextAreaBottom = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: -24px;
  span {
    margin-left: auto;
    line-height: 2;
  }
`;

const motives = ["position-filled", "position-cancelled"];

const reasons = [
  "no-budget",
  "redefined-profile",
  "re-arrangement",
  "covered-by-another-provider",
  "structure-change",
  "other"
];

const otherReasonMaxChars = 50;

export default function CloseOpportunityModal({ t, onSubmit, onClose }) {
  const closeOpportunityFormValidation = Yup.object().shape({
    closeMotive: Yup.string()
      .nullable()
      .required(t("form:emptyFieldValidation")),
    closeReason: Yup.string()
      .nullable()
      .when("closeMotive", {
        is: "position-cancelled",
        then: Yup.string().required(t("form:emptyFieldValidation")),
        otherwise: Yup.string()
      }),
    otherReason: Yup.string()
      .nullable()
      .when(["closeMotive", "closeReason"], {
        is: (closeMotive, closeReason) =>
          closeMotive === "position-cancelled" && closeReason === "other",
        then: Yup.string()
          .max(
            otherReasonMaxChars,
            t("form:maxCharsValidation", { chars: otherReasonMaxChars })
          )
          .required(t("form:emptyFieldValidation")),
        otherwise: Yup.string()
      })
  });

  return (
    <FormConfig
      onSubmit={onSubmit}
      initialValues={{
        closeMotive: null,
        closeReason: null,
        otherReason: null
      }}
      validationSchema={closeOpportunityFormValidation}
    >
      {({ handleSubmit, values }) => {
        const showCloseReason = values["closeMotive"] === "position-cancelled";
        const showOtherReason =
          showCloseReason && values["closeReason"] === "other";
        return (
          <Modal
            wrapProps={{
              "data-test": "close-opportunity-modal"
            }}
            visible={true}
            title={t("opportunity:publishing.close")}
            width={700}
            okText={t("opportunity:publishing.close")}
            onOk={handleSubmit}
            okButtonProps={{
              "data-test": "confirm-close-opportunity-btn"
            }}
            onCancel={onClose}
            cancelText={t("global:cancel")}
            destroyOnClose
          >
            <FormikForm>
              <Field
                name="closeMotive"
                label={t(
                  `opportunity:publishing.closeOpportunityForm.motive-label`
                )}
              >
                <Radio.Group buttonStyle="solid">
                  {motives.map(motive => (
                    <Radio.Button
                      key={motive}
                      value={motive}
                      data-test={`close-motive-${motive}`}
                    >
                      {t(
                        `opportunity:publishing.closeOpportunityForm.${motive}`
                      )}
                    </Radio.Button>
                  ))}
                </Radio.Group>
              </Field>
              {showCloseReason && (
                <Field
                  name="closeReason"
                  label={t(
                    `opportunity:publishing.closeOpportunityForm.reason-label`
                  )}
                >
                  <Select data-test="close-reason-select">
                    {reasons.map(reason => (
                      <Select.Option
                        key={reason}
                        value={reason}
                        data-test={`close-reason-option-${reason}`}
                      >
                        {t(
                          `opportunity:publishing.closeOpportunityForm.${reason}`
                        )}
                      </Select.Option>
                    ))}
                  </Select>
                </Field>
              )}
              {showOtherReason && (
                <>
                  <Field
                    name="otherReason"
                    label={t(
                      `opportunity:publishing.closeOpportunityForm.other-label`
                    )}
                    showError={false}
                  >
                    <Input.TextArea data-test="close-reason-other-input" />
                  </Field>
                  <TextAreaBottom>
                    <ErrorMessage name="otherReason" component={Message} />
                    <span>{`${
                      R.pathOr("", ["otherReason"], values).length
                    }/${otherReasonMaxChars}`}</span>
                  </TextAreaBottom>
                </>
              )}
            </FormikForm>
          </Modal>
        );
      }}
    </FormConfig>
  );
}
